/**
 * Nueva cotización x mayor — documento informativo multi-ítem para un
 * cliente mayorista (maestcta), molde de facturarxmayor.js SIN los efectos
 * de la factura: no marca chasis, no consume NCF, no valida límite de
 * crédito y el plan de cuotas es una simulación.
 *
 * Los ítems pueden ser chasis del inventario (validados disponibles con
 * /api/xmayor_validarchasis, precio editable, mínimo solo como advertencia)
 * o líneas libres con descripción, cantidad y precio unitario (mercancía en
 * tránsito o pedidos futuros).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { precioEnMoneda, monedaLabel as etiquetaMoneda } from "lib/moneda";
import { imprimirCotizacionXmayor } from "modules/cotizaciones/impresioncotizacionxmayor";

const filtrarClientes = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const filtrarProductos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n) => Math.round(n * 100) / 100;

const fmtFechaISO = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const hoyMasDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return fmtFechaISO(d);
};

// Suma meses a una fecha "YYYY-MM-DD" ajustando al último día del mes destino
// (misma regla que _cuotas_plan del backend).
const sumarMeses = (iso, meses) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, m - 1 + meses, 1);
  const ultimo = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(d, ultimo));
  return fmtFechaISO(dt);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Agregue los ítems a cotizar
      </Typography>
    </Stack>
  );
}

export default function CrearCotizacionXmayor({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [config, setConfig] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [cliente, setCliente] = useState(null);
  const [tipofactura, setTipofactura] = useState("Contado");
  const [moneda, setMoneda] = useState("P");
  const [tasa, setTasa] = useState("");
  const [validez, setValidez] = useState("15");
  const [comentario, setComentario] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [items, setItems] = useState([]);
  const [prodSel, setProdSel] = useState(null);
  const [plazos, setPlazos] = useState("1");
  const [fechaprimerpago, setFechaprimerpago] = useState(hoyMasDias(30));
  const [validando, setValidando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [empresa, setEmpresa] = useState("1");

  // Línea libre (mercancía sin chasis): descripción + cantidad + precio.
  const [libreDesc, setLibreDesc] = useState("");
  const [libreCant, setLibreCant] = useState("1");
  const [librePrecio, setLibrePrecio] = useState("");

  const cargarProductos = async (emp = empresa) => {
    const { ok, data } = await post(
      "/api/xmayor_productos",
      Number(emp) > 0 ? { empresa: Number(emp) } : {}
    );
    if (ok) setProductos(data.productos || []);
  };

  const cargar = async () => {
    setCargando(true);
    try {
      const [conf, cli, prod] = await Promise.all([
        post("/api/xmayor_config", {}),
        post("/api/xmayor_clientes", {}),
        post("/api/xmayor_productos", {}),
      ]);
      if (conf.ok) {
        setConfig(conf.data);
        setTasa(String(conf.data.tasaus ?? ""));
        if (conf.data.multiempresa) await cargarProductos("1");
      } else {
        showAlert({
          message: conf.data.Error || "No se pudo cargar la configuración",
          color: "error",
        });
      }
      if (cli.ok) setClientes(cli.data.clientes || []);
      if (prod.ok) setProductos(prod.data.productos || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usaMultiempresa = !!config?.multiempresa;
  const empresasOpc = config?.empresas || [];

  const cambiarEmpresa = async (v) => {
    setEmpresa(v);
    // Los chasis agregados pertenecen a la empresa anterior: se descartan
    // (las líneas libres se conservan, no dependen del inventario).
    setItems((prev) => prev.filter((i) => !i.chasis));
    setProdSel(null);
    await cargarProductos(v);
  };

  const monedaLabel = etiquetaMoneda(moneda);
  // Moneda en la que están expresados producto.precio/mprecio. Sin catálogo
  // de monedas el backend responde "DOP" y todo se comporta como siempre.
  const monedaBase = config?.base || "DOP";

  const disponibles = useMemo(
    () => productos.filter((p) => !items.some((i) => i.chasis === p.chasis)),
    [productos, items]
  );

  const total = useMemo(
    () => round2(items.reduce((acc, i) => acc + toNum(i.cantidad) * toNum(i.preciounitario), 0)),
    [items]
  );

  // El mínimo del chasis solo advierte: cotizar no compromete la venta.
  const minimoEnMoneda = (i) => precioEnMoneda(i.mprecio, moneda, tasa, monedaBase);
  const bajoElMinimo = (i) =>
    !!i.chasis && minimoEnMoneda(i) > 0 && toNum(i.preciounitario) < minimoEnMoneda(i);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hayBajoMinimo = useMemo(() => items.some(bajoElMinimo), [items, moneda, tasa]);

  const cuotasPreview = useMemo(() => {
    if (tipofactura !== "Credito") return [];
    const n = parseInt(plazos, 10) || 0;
    if (n < 1 || n > 60 || total <= 0 || !fechaprimerpago) return [];
    const cuota = round2(total / n);
    const filas = [];
    for (let i = 0; i < n; i += 1) {
      filas.push({
        numero: i + 1,
        fecha: sumarMeses(fechaprimerpago, i),
        valor: i === n - 1 ? round2(total - cuota * (n - 1)) : cuota,
      });
    }
    return filas;
  }, [tipofactura, plazos, total, fechaprimerpago]);

  const limpiar = () => {
    setCliente(null);
    setTipofactura("Contado");
    setMoneda("P");
    setTasa(String(config?.tasaus ?? ""));
    setValidez("15");
    setComentario("");
    setVendedor("");
    setItems([]);
    setProdSel(null);
    setPlazos("1");
    setFechaprimerpago(hoyMasDias(30));
    setLibreDesc("");
    setLibreCant("1");
    setLibrePrecio("");
  };

  const agregarChasis = async (prod) => {
    setProdSel(null);
    if (!prod) return;
    if (items.some((i) => i.chasis === prod.chasis)) {
      showAlert({ message: "El chasis ya está agregado a la cotización", color: "warning" });
      return;
    }
    setValidando(true);
    try {
      const { ok, data } = await post("/api/xmayor_validarchasis", {
        chasis: prod.chasis,
        ...(usaMultiempresa ? { empresa: Number(empresa) } : {}),
      });
      if (!ok) {
        showAlert({ message: data.Error || "El chasis no está disponible", color: "error" });
        return;
      }
      const precio = precioEnMoneda(prod.precio, moneda, tasa, monedaBase);
      setItems((prev) => [
        ...prev,
        {
          key: prod.chasis,
          chasis: prod.chasis,
          descripcion: [prod.marca, prod.modelo, prod.colore].filter(Boolean).join(" "),
          cantidad: "1",
          preciounitario: precio.toFixed(2),
          mprecio: toNum(prod.mprecio),
        },
      ]);
    } finally {
      setValidando(false);
    }
  };

  const agregarLibre = () => {
    const desc = libreDesc.trim();
    const cant = parseInt(libreCant, 10) || 0;
    const precio = toNum(librePrecio);
    if (!desc) {
      showAlert({ message: "Indique la descripción del ítem", color: "warning" });
      return;
    }
    if (cant < 1) {
      showAlert({ message: "La cantidad debe ser mayor que 0", color: "warning" });
      return;
    }
    if (precio <= 0) {
      showAlert({ message: "Indique el precio unitario", color: "warning" });
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        key: `libre-${Date.now()}`,
        chasis: "",
        descripcion: desc,
        cantidad: String(cant),
        preciounitario: precio.toFixed(2),
        mprecio: 0,
      },
    ]);
    setLibreDesc("");
    setLibreCant("1");
    setLibrePrecio("");
  };

  const actualizarItem = (key, campo, v) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, [campo]: v } : i)));
  };

  const quitarItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const guardar = async () => {
    if (!cliente) {
      showAlert({ message: "Debe seleccionar el cliente mayorista", color: "warning" });
      return;
    }
    if (items.length === 0) {
      showAlert({ message: "Agregue al menos un ítem a la cotización", color: "warning" });
      return;
    }
    if (moneda === "U" && toNum(tasa) <= 0) {
      showAlert({ message: "Indique la tasa para cotizar en USD", color: "warning" });
      return;
    }
    if (items.some((i) => toNum(i.preciounitario) <= 0)) {
      showAlert({ message: "Todos los ítems deben tener precio", color: "warning" });
      return;
    }
    const nPlazos = parseInt(plazos, 10) || 0;
    if (tipofactura === "Credito" && (nPlazos < 1 || nPlazos > 60)) {
      showAlert({ message: "Los plazos deben estar entre 1 y 60", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cotizacionxmayor_crear", {
        idcliente: cliente.id,
        ...(usaMultiempresa ? { empresa: Number(empresa) } : {}),
        tipofactura,
        moneda,
        tasa: toNum(tasa),
        validez,
        comentario: comentario.trim(),
        vendedor: vendedor.trim(),
        items: items.map((i) =>
          i.chasis
            ? { chasis: i.chasis, preciounitario: toNum(i.preciounitario) }
            : {
                descripcion: i.descripcion,
                cantidad: parseInt(i.cantidad, 10) || 1,
                preciounitario: toNum(i.preciounitario),
              }
        ),
        plazos: tipofactura === "Credito" ? nPlazos : 0,
        fechaprimerpago,
      });
      if (ok) {
        showAlert({
          message: `${data.mensajeok || "Cotización registrada"} — #${data.id}`,
          color: "success",
        });
        imprimirCotizacionXmayor(data);
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la cotización", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columns = [
    {
      field: "cantidad",
      headerName: "Cant.",
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.chasis ? (
          <Typography sx={{ fontSize: "0.85rem" }}>1</Typography>
        ) : (
          <TextField
            size="small"
            type="number"
            value={params.row.cantidad}
            onChange={(e) => actualizarItem(params.row.key, "cantidad", e.target.value)}
            inputProps={{ min: 1, style: { textAlign: "right" } }}
            sx={fpInputSx}
          />
        ),
    },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 180 },
    { field: "chasis", headerName: "Chasis", width: 160, valueGetter: (v) => v || "—" },
    {
      field: "preciounitario",
      headerName: "Precio Unit.",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.preciounitario}
          onChange={(e) => actualizarItem(params.row.key, "preciounitario", e.target.value)}
          error={bajoElMinimo(params.row)}
          title={
            minimoEnMoneda(params.row) > 0
              ? `Precio mínimo ${fmtMoney(minimoEnMoneda(params.row))}`
              : ""
          }
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "importe",
      headerName: "Importe",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueGetter: (_v, row) => round2(toNum(row.cantidad) * toNum(row.preciounitario)),
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => quitarItem(params.row.key)} title="Quitar">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnasCuotas = [
    { field: "numero", headerName: "Cuota", width: 90 },
    { field: "fecha", headerName: "Fecha", flex: 1, minWidth: 120 },
    {
      field: "valor",
      headerName: "Valor",
      width: 150,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cotización x Mayor"
        subtitle="Documento informativo multi-ítem; no compromete la venta ni reserva los chasis"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nueva
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Datos de la cotización */}
        <Box sx={sectionBox}>
          <SectionTitle icon={RequestQuoteOutlinedIcon} color={FP.color.accentBlue}>
            Datos de la Cotización
          </SectionTitle>
          <Grid container spacing={2}>
            {usaMultiempresa && (
              <>
                <Grid item xs={12} sm={5} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Empresa"
                    value={empresa}
                    onChange={(e) => cambiarEmpresa(e.target.value)}
                    sx={fpInputSx}
                  >
                    {empresasOpc.map((e2) => (
                      <MenuItem key={e2.empresa} value={String(e2.empresa)}>
                        {e2.empresa} — {e2.nombre || `Empresa ${e2.empresa}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item sm={7} md={9} sx={{ display: { xs: "none", sm: "block" } }} />
              </>
            )}
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarClientes}
                options={clientes}
                loading={cargando}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setCliente(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.id} — {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.rnc ? `RNC ${o.rnc}` : ""].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente mayorista" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={tipofactura}
                onChange={(e) => setTipofactura(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Credito">Credito</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Moneda"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                disabled={items.length > 0}
                helperText={items.length > 0 ? "Bloqueada con ítems agregados" : undefined}
                sx={fpInputSx}
              >
                <MenuItem value="P">{etiquetaMoneda("P")}</MenuItem>
                <MenuItem value="U">USD</MenuItem>
              </TextField>
            </Grid>
            {moneda === "U" && (
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Tasa"
                  value={tasa}
                  onChange={(e) => setTasa(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ style: { textAlign: "right" } }}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Validez (días)"
                value={validez}
                onChange={(e) => setValidez(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Vendedor"
                value={vendedor}
                onChange={(e) => setVendedor(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Ítems */}
        <Box sx={sectionBox}>
          <SectionTitle
            icon={DirectionsCarFilledOutlinedIcon}
            color={FP.color.success}
            extra={
              <Chip
                label={`Total: ${monedaLabel} ${fmtMoney(total)}`}
                sx={{
                  ml: "auto",
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            }
          >
            Ítems
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarProductos}
                options={disponibles}
                loading={cargando || validando}
                value={prodSel}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : `${o.chasis} — ${[o.marca, o.modelo].filter(Boolean).join(" ")}`
                }
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                onChange={(_, v) => agregarChasis(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[
                          [o.marca, o.modelo, o.colore].filter(Boolean).join(" "),
                          `${monedaLabel} ${fmtMoney(
                            precioEnMoneda(o.precio, moneda, tasa, monedaBase)
                          )}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agregar chasis del inventario"
                    helperText="Busque por chasis, marca o modelo"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <TextField
                  label="Línea libre (descripción)"
                  value={libreDesc}
                  onChange={(e) => setLibreDesc(e.target.value)}
                  sx={{ ...fpInputSx, flex: 1 }}
                />
                <TextField
                  label="Cant."
                  type="number"
                  value={libreCant}
                  onChange={(e) => setLibreCant(e.target.value)}
                  inputProps={{ min: 1, style: { textAlign: "right" } }}
                  sx={{ ...fpInputSx, width: 90 }}
                />
                <TextField
                  label="Precio unit."
                  value={librePrecio}
                  onChange={(e) => setLibrePrecio(e.target.value)}
                  inputProps={{ style: { textAlign: "right" } }}
                  sx={{ ...fpInputSx, width: 130 }}
                />
                <IconButton
                  onClick={agregarLibre}
                  title="Agregar línea"
                  sx={{
                    mt: 0.6,
                    bgcolor: FP.color.navy,
                    color: "#fff",
                    "&:hover": { bgcolor: FP.color.navySoft },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={items}
              columns={columns}
              getRowId={(r) => r.key}
              rowHeight={52}
              hideFooter
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
          {hayBajoMinimo && (
            <Typography
              sx={{ mt: 1.4, fontWeight: 700, fontSize: "0.82rem", color: FP.color.warning }}
            >
              Hay precios por debajo del mínimo del chasis (solo advertencia: la cotización no
              compromete la venta)
            </Typography>
          )}
        </Box>

        {/* Plan de pago simulado (solo crédito) */}
        {tipofactura === "Credito" && (
          <Box sx={sectionBox}>
            <SectionTitle icon={CalendarTodayOutlinedIcon} color={FP.color.navy}>
              Plan de pago (simulado)
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Plazos"
                  value={plazos}
                  onChange={(e) => setPlazos(e.target.value)}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="Cuotas mensuales (1 a 60)"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha primer pago"
                  value={fechaprimerpago}
                  onChange={(e) => setFechaprimerpago(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                {cuotasPreview.length > 0 && (
                  <Chip
                    label={`${cuotasPreview.length} cuota(s) de ${monedaLabel} ${fmtMoney(
                      cuotasPreview[0].valor
                    )}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Grid>
              {cuotasPreview.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#fff" }}>
                    <DataGrid
                      autoHeight
                      rows={cuotasPreview}
                      columns={columnasCuotas}
                      getRowId={(r) => r.numero}
                      rowHeight={40}
                      hideFooter
                      sx={fpDataGridSx}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Guardar */}
        <Box sx={{ ...sectionBox, mb: 0, display: "flex", justifyContent: "flex-end" }}>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando || cargando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar e imprimir
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
